import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { pushLocation } from 'favoriteList/store/__router/actions';

export type FormSubmit = {
  defaultSubmit: () => void;
  query: string;
  queryObj: { [key: string]: FormDataEntryValue };
};

export type Props = {
  className?: string;
  action?: string;
  onSubmit?: ({ defaultSubmit, query, queryObj }: FormSubmit) => void;
  children: React.ReactNode;
} & Omit<React.HTMLProps<HTMLFormElement>, 'onSubmit'>;

export function formDataToObject(formData: FormData) {
  const object: { [key: string]: FormDataEntryValue } = {};
  for (const [key, value] of Array.from(formData.entries())) {
    object[key] = value;
  }

  return object;
}

function Form(props: Props) {
  const { action, onSubmit, children, ...rest } = props;
  const dispatch = useDispatch();
  const formEl = useRef<HTMLFormElement | null>(null);

  const submitForm = useCallback(
    (event) => {
      event.preventDefault();

      if (formEl.current) {
        const formData = new FormData(formEl.current);
        const queryString = new URLSearchParams(formData as any).toString();
        const queryObj = formDataToObject(formData);

        const defaultSubmit = () => {
          // defaultSubmit will be executed if action prop exists
          if (action) {
            dispatch(
              pushLocation({
                pathname: action,
                search: queryString,
              }),
            );
          }
        };

        if (onSubmit) onSubmit({ defaultSubmit, query: queryString, queryObj });
        else defaultSubmit();
      }
    },
    [action, dispatch, onSubmit],
  );

  return (
    <form onSubmit={submitForm} ref={formEl} action={action} {...rest}>
      {children}
    </form>
  );
}

export default Form;
